import 'polyfill-object.fromentries';
import 'url-search-params-polyfill';
import './tailwind.css';

import * as Sentry from '@sentry/react';
import { Storage } from '@wren/shared';
import { hasConsent } from '@wren/shared';
import posthog from 'posthog-js';
import type { PostHogInterface } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { createRoot } from 'react-dom/client';
import ErrorMessage from './components/ErrorMessage';
import * as AB from './lib/ab';
import {
  initAnalyticsScripts,
  isAnalyticsInitialized,
} from './lib/consentedScripts';
import track from './lib/events';
import { installStaleChunkRecovery } from './lib/staleChunkRecovery';
import patchNode from './patches/node';
import Root from './Root';
import { initializeStore } from './store';

const { hasLocalStorage } = Storage;

installStaleChunkRecovery();

declare global {
  interface Window {
    appVersion: string | undefined;
    plausible: (
      eventName: string,
      properties?: Record<string, unknown>
    ) => void;
    posthog: PostHogInterface | undefined;
  }
}

async function run() {
  if (hasConsent('analytics')) {
    initAnalyticsScripts();
  }

  const store = await initializeStore();

  if (!hasLocalStorage()) {
    store.dispatch({
      type: 'SET_TOAST',
      toast: {
        type: 'alert',
        text: "We noticed that your browser doesn't allow our website to store data on your computer (cookies). This means you will lose all your progress when you leave the site.",
      },
    });
  }

  track('Server: User Arrived On Page', {
    location: window.location.pathname,
  });

  const { userId } = store.getState().user;

  if (isAnalyticsInitialized() && !posthog._isIdentified()) {
    posthog.identify(userId as unknown as string);
  }

  AB.assignExperiments(userId);

  document.documentElement.prepend(
    document.createComment('Work at Wren: ZXJpY0Bwcm9qZWN0d3Jlbi5jb20=')
  );

  patchNode();

  createRoot(document.getElementById('root')!).render(
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <ErrorMessage error={error} resetError={resetError} />
      )}
    >
      <PostHogProvider client={posthog}>
        <Root store={store} />
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  );
}

run();
